/* Tax invoice preview + print (prototype invoiceModal) and receipt viewer. */
import { useData } from "../../data";
import type { Payment } from "../../data/types";
import { useI18n } from "../../i18n";
import { fmtN, gDate } from "../../lib/format";
import { Button, Modal } from "../../ui";
import { sampleReceiptSvg } from "./receipt";

/** Deterministic pseudo-QR (visual placeholder for the ZATCA QR). */
function PseudoQr({ seed, size = 84 }: { seed: string; size?: number }) {
  let h = 2166136261;
  for (const ch of seed) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  const n = 21, cells: [number, number][] = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const finder = (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7);
    if (finder) { const fx = x % 14 < 7 ? x : x - (n - 7), fy = y < 7 ? y : y - (n - 7); const d = Math.max(Math.abs(fx - 3), Math.abs(fy - 3)); if (d === 3 || d <= 1) cells.push([x, y]); continue; }
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5; h >>>= 0;
    if (h % 2) cells.push([x, y]);
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} role="img" aria-label="ZATCA QR">
      <rect width={n} height={n} fill="#fff" />
      {cells.map(([x, y]) => <rect key={x + "-" + y} x={x} y={y} width={1} height={1} fill="#111" />)}
    </svg>
  );
}

export const invoiceNo = (p: Payment) => p.id.replace("PAY", "INV");

export function InvoiceModal({ p, onClose }: { p: Payment; onClose: () => void }) {
  const { t, L } = useI18n();
  return (
    <Modal title={L("Tax invoice", "فاتورة ضريبية") + " · " + invoiceNo(p)} className="wide" onClose={onClose} testId="invoice-modal"
      foot={<><Button variant="ghost" onClick={onClose}>{t("Close")}</Button><Button variant="pri" icon="printer" onClick={() => window.print()} data-testid="invoice-print">{t("Print")}</Button></>}>
      <article className="paper" dir="ltr" data-testid="invoice-paper">
        <div className="paper-head">
          <div><h2>StartHR Technologies Co.</h2><div className="muted" style={{ fontSize: 12 }}>VAT 311234567800003 · CR 1010999888 · Riyadh</div></div>
          <div style={{ textAlign: "end" }}><b>TAX INVOICE</b><div className="ar">فاتورة ضريبية</div><div className="muted" style={{ fontSize: 12 }}>{gDate(p.at || p.date, "en")}</div></div>
        </div>
        <div className="bi" style={{ marginBottom: 12 }}>
          <div><div className="muted" style={{ fontSize: 12 }}>Bill to</div><b>{p.tenant}</b><div style={{ fontSize: 12 }}>CR {p.cr}</div></div>
          <div style={{ textAlign: "end" }}><div className="muted" style={{ fontSize: 12 }}>Invoice no.</div><b className="num">{invoiceNo(p)}</b></div>
        </div>
        <table>
          <thead><tr><th>Description</th><th className="end">Qty</th><th className="end">Amount</th></tr></thead>
          <tbody><tr><td>StartHR {p.plan} plan · {p.cycle === "y" ? "12 months" : "1 month"}</td><td className="end num">{p.seats}</td><td className="end num">{fmtN(p.amount)}</td></tr></tbody>
          <tfoot>
            <tr><td colSpan={2}>VAT 15%</td><td className="end num">{fmtN(p.vat)}</td></tr>
            <tr className="tot"><td colSpan={2}>Total (SAR)</td><td className="end num" data-testid="invoice-total">{fmtN(p.total)}</td></tr>
          </tfoot>
        </table>
        <div className="row between" style={{ marginTop: 16, alignItems: "flex-end" }}>
          <div className="muted" style={{ fontSize: 11.5 }}>Paid by bank transfer · {p.ref}<br />ZATCA e-invoice QR (sample)</div>
          <PseudoQr seed={"INV" + p.id} />
        </div>
      </article>
    </Modal>
  );
}

export function ReceiptModal({ p, onClose }: { p: Payment; onClose: () => void }) {
  const { t, L } = useI18n();
  const data = useData();
  const src = p.receiptKey ? data.fileUrl(p.receiptKey) : sampleReceiptSvg({ paid: p.paid, ref: p.ref, date: p.date, bank: p.bank, id: p.id });
  return (
    <Modal title={L("Receipt", "الإيصال") + " · " + p.id} onClose={onClose} testId="receipt-modal" foot={<Button onClick={onClose}>{t("Close")}</Button>}>
      <img src={src} alt={L("Bank transfer receipt", "إيصال التحويل البنكي")} style={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }} />
    </Modal>
  );
}
